'use client';

import { useRef } from 'react';

import Link from 'next/link';

import { APIImageType } from '@/types';
import { Logo } from '@/ui';
import { wrap } from '@motionone/utils';
import clsx from 'clsx';
import {
  motion,
  useAnimationFrame,
  useMotionValue,
  useScroll,
  useSpring,
  useTransform,
  useVelocity,
} from 'framer-motion';

import { imageMetaConverter } from '@/lib/server';

interface Logo {
  name: string;
  logo: APIImageType[];
  url: string;
}

interface Props {
  logos: Logo[];
  scrollSpeed: number;
  className?: string;
}

export function LogoCarousel({ logos, className, scrollSpeed = 1 }: Props) {
  const baseX = useMotionValue(0);
  const { scrollY } = useScroll();
  const scrollVelocity = useVelocity(scrollY);
  const smoothVelocity = useSpring(scrollVelocity, {
    damping: 50,
    stiffness: 400,
  });
  const velocityFactor = useTransform(smoothVelocity, [0, 1000], [0, 5], {
    clamp: false,
  });
  const x = useTransform(baseX, (v) => `${wrap(-20, -45, v)}%`);

  const directionFactor = useRef<number>(1);
  useAnimationFrame((t, delta) => {
    let moveBy = directionFactor.current * scrollSpeed * (delta / 1000);

    if (velocityFactor.get() < 0) {
      directionFactor.current = -0.75;
    } else if (velocityFactor.get() > 0) {
      directionFactor.current = 0.75;
    }

    moveBy += directionFactor.current * moveBy * velocityFactor.get();
    baseX.set(baseX.get() + moveBy);
  });

  return (
    <div className={clsx('w-full overflow-hidden', className)}>
      <div className="parallax">
        <motion.div className="scroller flex w-max gap-16" style={{ x }}>
          {Array(4).fill(
            logos.map((logo) => {
              const image = imageMetaConverter(logo.logo);

              return (
                <div key={logo.name} className="flex flex-nowrap gap-16">
                  <Link title={logo.name} href={logo.url}>
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img src={image?.src} alt={image?.alt} />
                  </Link>
                </div>
              );
            }),
          )}
        </motion.div>
      </div>
    </div>
  );
}
